import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { IStatistic2Report, IStatistic2ReportItem } from '@fi-app/statistic2/statistic2';
import {
    IStatistic2ReportViewParams,
    IStatisticReportTableSettings,
} from '@fi-app/statistic2/statistic2-report-view/statistic2-report-view';
import { BehaviorSubject, interval } from 'rxjs';
import { Subject } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { Statistic2HelperService } from '@fi-app/statistic2/statistic2-helper.service';
import { ObjectTypes } from '@fi-shared/types';
import { Statistic2ReportColumn } from '@fi-app/statistic2/statistic2-report-view/statistic2-report-column';
import { Statistic2ReportType, Statistic2UserSelectionType } from '@fi-app/statistic2/statistic2-types';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';

@Component({
    selector: 'fi-statistic2-report-view-list',
    templateUrl: './statistic2-report-view-list.component.html',
    styleUrls: ['./statistic2-report-view-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-statistic2-report-view-list' },
    standalone: false,
})
export class Statistic2ReportViewListComponent
    extends FiInfiniteScrollDataSource<IStatistic2ReportItem>
    implements OnInit, OnDestroy
{
    @Input() reportViewParams: IStatistic2ReportViewParams;
    @Input() filterText$ = new BehaviorSubject<string>('');
    @Input() isOverflowScroll = false;
    @Input() isWideSize = false;
    @Output() reportItemClick = new EventEmitter<IStatistic2ReportItem>();
    FiStateNames = FiStateNames;
    FiProfileSizes = FiProfileSizes;
    statisticReportTableSettings: IStatisticReportTableSettings[] = [];
    tooltipMode = FiInfoIconMode.WithoutIconAbsolute;

    objectType = ObjectTypes.Statistic2;
    itemsListType = FiItemsListType.Statistics;

    constructor(
        private statisticHelperService: Statistic2HelperService,
        injector: Injector,
    ) {
        super(injector, { reverseMode: false });
    }

    ngOnInit() {
        this.defaultSortColumnName = this.getDefaultSortColumnName();
        this.generateTableSettings();
        this.reloadData().then(() => {
            this.subscribeOnFilterChange();
        });
    }

    getListData(): Promise<IStatistic2ReportItem[]> {
        return new Promise<IStatistic2ReportItem[]>((resolve) => {
            this.reportViewParams.loadItemsPromise.then((reportsData: IStatistic2Report) => {
                const filteredData = this.getNewTableItemsData(reportsData.Data, this.filterText$.value);
                resolve(filteredData);
            });
        });
    }

    isClickableCell(column: IStatisticReportTableSettings): boolean {
        return this.statisticHelperService.isColumnType(column.columnType, [
            Statistic2ReportColumn.Unit,
            Statistic2ReportColumn.ObjectName,
        ]);
    }

    private getDefaultSortColumnName(): string {
        return this.isIndividualLoginPage() ? 'StartDate' : 'ObjectName';
    }

    private isIndividualLoginPage(): boolean {
        return (
            this.reportViewParams.reportParameter.ReportType === Statistic2ReportType.UserLogins &&
            this.reportViewParams.reportParameter.UserSelectionType === Statistic2UserSelectionType.Users
        );
    }

    private subscribeOnFilterChange() {
        this.filterText$
            .pipe(
                debounce(() => interval(500)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => {
                this.reloadData();
            });
    }

    private generateTableSettings() {
        this.statisticReportTableSettings = this.statisticHelperService.generateTableData(this.reportViewParams);
    }

    onReportItemClick($event, item: IStatistic2ReportItem) {
        $event.stopPropagation();
        this.reportItemClick.next(item);
    }

    private getNewTableItemsData(allData: IStatistic2ReportItem[], filterText: string): IStatistic2ReportItem[] {
        return !filterText ? allData : this.filterInitialData(allData, filterText);
    }

    private filterInitialData(allData: IStatistic2ReportItem[], filterText: string): IStatistic2ReportItem[] {
        return allData.filter((item: IStatistic2ReportItem) => {
            return (
                this.isMatch(item.ObjectName, filterText) ||
                this.isMatch(item.BusinessUnitName, filterText) ||
                this.isMatch(item.AreaName, filterText) ||
                (item.Roles && item.Roles.find((role) => this.isMatch(role.Name, filterText)))
            );
        });
    }

    private isMatch(value: string, searchText: string) {
        return (value || '').toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
